import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Settings as SettingsIcon, Palette, Layout, Save, RotateCcw, Sun, Moon } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Settings({ isOpen, onClose }) {
  const { 
    theme, 
    themes, 
    currentTheme, 
    updateTheme, 
    customColor,
    updateCustomColor,
    isDarkMode,
    toggleDarkMode,
    layout, 
    toggleTab, 
    reorderTabs,
    enabledTabs 
  } = useTheme();
  
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('theme');
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Используем переданные пропсы или внутреннее состояние
  const modalIsOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const handleClose = onClose || (() => setInternalIsOpen(false));

  const handleThemeChange = (newTheme) => {
    updateTheme(newTheme);
    if (newTheme === 'custom') {
      setShowColorPicker(true);
    } else {
      setShowColorPicker(false);
    }
    toast.success(`Тема изменена на "${themes[newTheme].name}"`);
  };

  const handleCustomColorChange = (event) => {
    const newColor = event.target.value;
    
    // Валидация HEX кода
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (newColor.length === 7 && !hexRegex.test(newColor)) {
      toast.error('Неверный формат HEX кода');
      return;
    }
    
    updateCustomColor(newColor);
  };

  const handleDarkModeToggle = () => {
    toggleDarkMode();
    toast.success(`Переключено на ${isDarkMode ? 'светлую' : 'темную'} тему`);
  };

  const handleTabToggle = (tabId) => {
    toggleTab(tabId);
    toast.success('Настройки вкладок обновлены');
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    if (dragIndex !== dropIndex) {
      reorderTabs(dragIndex, dropIndex);
      toast.success('Порядок вкладок изменен');
    }
  };

  const resetToDefault = () => {
    const defaultLayout = {
      tabs: [
        { id: 'dashboard', label: 'Дашборд', icon: '📊', enabled: true },
        { id: 'players', label: 'Игроки', icon: '👥', enabled: true },
        { id: 'punishments', label: 'Наказания', icon: '⚖️', enabled: true },
        { id: 'active-bans', label: 'Баны', icon: '🚫', enabled: true },
        { id: 'reports', label: 'Жалобы', icon: '🚨', enabled: true },
        { id: 'commands', label: 'Команды', icon: '⌨️', enabled: true },
        { id: 'pvplogs', label: 'PvP Логи', icon: '⚔️', enabled: true },
        { id: 'staff', label: 'Стафф', icon: '👑', enabled: true },
        { id: 'tags', label: 'Теги', icon: '🏷️', enabled: true },
        { id: 'map', label: '3D Карта', icon: '🗺️', enabled: true },
        { id: 'observation', label: 'Наблюдение', icon: '👁️', enabled: true }
      ]
    };
    localStorage.setItem('layout', JSON.stringify(defaultLayout));
    window.location.reload();
    toast.success('Настройки сброшены к значениям по умолчанию');
  };

  return (
    <>
      {/* Кнопка настроек (только если не переданы пропсы) */}
      {isOpen === undefined && (
        <button
          onClick={() => setInternalIsOpen(true)}
          className="fixed top-6 right-6 z-50 bg-gmod-lighter hover:bg-gmod-lighterer border border-gmod-lighterer rounded-full p-3 text-gmod-white transition-all duration-200 hover:scale-110 shadow-lg"
          title="Настройки"
        >
          <SettingsIcon size={24} />
        </button>
      )}

      {/* Модальное окно настроек */}
      {modalIsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-gmod-dark border border-gmod-lighterer rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gmod-white">Настройки</h2>
              <button
                onClick={handleClose}
                className="text-gmod-white hover:text-theme-primary transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Вкладки настроек */}
            <div className="flex space-x-1 mb-6">
              <button
                onClick={() => setActiveTab('theme')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'theme' 
                    ? 'bg-theme-primary text-white' 
                    : 'bg-gmod-lighter text-gmod-white hover:bg-gmod-lighterer'
                }`}
              >
                <Palette size={16} />
                Тема
              </button>
              <button
                onClick={() => setActiveTab('appearance')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'appearance' 
                    ? 'bg-theme-primary text-white' 
                    : 'bg-gmod-lighter text-gmod-white hover:bg-gmod-lighterer'
                }`}
              >
                {isDarkMode ? <Moon size={16} /> : <Sun size={16} />}
                Внешний вид
              </button>
              <button
                onClick={() => setActiveTab('layout')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'layout' 
                    ? 'bg-theme-primary text-white' 
                    : 'bg-gmod-lighter text-gmod-white hover:bg-gmod-lighterer'
                }`}
              >
                <Layout size={16} />
                Макет
              </button>
            </div>

            {/* Содержимое вкладок */}
            {activeTab === 'theme' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gmod-white mb-4">Выберите тему</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(themes).map(([key, themeData]) => (
                      <button
                        key={key}
                        onClick={() => handleThemeChange(key)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          theme === key
                            ? 'border-theme-primary bg-theme-primary-alpha'
                            : 'border-gmod-lighterer bg-gmod-lighter hover:border-gmod-lightest'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: key === 'custom' ? customColor : themeData.primary }}
                          />
                          <div className="text-left">
                            <div className="text-gmod-white font-medium">{themeData.name}</div>
                            <div className="text-xs text-gmod-white opacity-60">
                              {key === 'custom' ? customColor : themeData.primary}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Цветовой пикер для пользовательской темы */}
                  {theme === 'custom' && (
                    <div className="mt-6 p-4 bg-gmod-lighter rounded-lg border border-gmod-lighterer">
                      <h4 className="text-gmod-white font-medium mb-3">Выберите свой цвет</h4>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={customColor}
                          onChange={handleCustomColorChange}
                          className="w-12 h-12 rounded-lg border-2 border-gmod-lighterer cursor-pointer"
                        />
                        <div className="flex-1">
                          <input
                            type="text"
                            value={customColor}
                            onChange={handleCustomColorChange}
                            className="w-full px-3 py-2 bg-gmod-dark border border-gmod-lighterer rounded-md text-gmod-white font-mono text-sm"
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gmod-white opacity-60 mt-2">
                        Введите HEX код цвета (например: #ff0000)
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gmod-white mb-4">Предпросмотр</h3>
                  <div className="bg-gmod-lighter rounded-lg p-4 space-y-3">
                    <div className="flex gap-2">
                      <button className="bg-theme-primary hover:bg-theme-primary-hover text-white px-4 py-2 rounded-md transition-colors">
                        Основная кнопка
                      </button>
                      <button className="bg-gmod-lighterer hover:bg-gmod-lightest text-gmod-white px-4 py-2 rounded-md transition-colors">
                        Вторичная кнопка
                      </button>
                    </div>
                    <div className="h-2 bg-theme-primary rounded"></div>
                    <div className="text-theme-primary font-medium">Текст в цвете темы</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gmod-white mb-4">Темная/Светлая тема</h3>
                  <p className="text-gmod-white opacity-60 mb-4">
                    Переключите между темной и светлой темой интерфейса
                  </p>
                  
                  <div className="bg-gmod-lighter rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isDarkMode ? (
                          <Moon className="w-6 h-6 text-theme-primary" />
                        ) : (
                          <Sun className="w-6 h-6 text-theme-primary" />
                        )}
                        <div>
                          <div className="text-gmod-white font-medium">
                            {isDarkMode ? 'Темная тема' : 'Светлая тема'}
                          </div>
                          <div className="text-sm text-gmod-white opacity-60">
                            {isDarkMode ? 'Темные цвета для комфортного использования в темноте' : 'Светлые цвета для использования при ярком освещении'}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleDarkModeToggle}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          isDarkMode ? 'bg-theme-primary' : 'bg-gmod-lighterer'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isDarkMode ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gmod-white mb-4">Предпросмотр</h3>
                  <div className="bg-gmod-lighter rounded-lg p-4 space-y-3">
                    <div className="flex gap-2">
                      <button className="bg-theme-primary hover:bg-theme-primary-hover text-white px-4 py-2 rounded-md transition-colors">
                        Основная кнопка
                      </button>
                      <button className="bg-gmod-lighterer hover:bg-gmod-lightest text-gmod-white px-4 py-2 rounded-md transition-colors">
                        Вторичная кнопка
                      </button>
                    </div>
                    <div className="h-2 bg-theme-primary rounded"></div>
                    <div className="text-theme-primary font-medium">Текст в цвете темы</div>
                    <div className="text-gmod-white opacity-60">Дополнительный текст</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'layout' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gmod-white mb-4">Управление вкладками</h3>
                  <p className="text-gmod-white opacity-60 mb-4">
                    Перетащите вкладки для изменения порядка или отключите ненужные
                  </p>
                  
                  <div className="space-y-2">
                    {layout.tabs.map((tab, index) => (
                      <div
                        key={tab.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className="flex items-center gap-3 p-3 bg-gmod-lighter rounded-lg border border-gmod-lighterer hover:bg-gmod-lighterer transition-colors cursor-move"
                      >
                        <div className="text-lg">📋</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{tab.icon}</span>
                            <span className="text-gmod-white font-medium">{tab.label}</span>
                          </div>
                        </div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={tab.enabled}
                            onChange={() => handleTabToggle(tab.id)}
                            className="w-4 h-4 text-theme-primary bg-gmod-dark border-gmod-lighterer rounded focus:ring-theme-primary focus:ring-2"
                          />
                          <span className="text-gmod-white text-sm">
                            {tab.enabled ? 'Включена' : 'Отключена'}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={resetToDefault}
                    className="flex items-center gap-2 px-4 py-2 bg-gmod-red hover:bg-red-700 text-white rounded-md transition-colors"
                  >
                    <RotateCcw size={16} />
                    Сбросить к умолчанию
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
} 